import { useCallback, useReducer } from "react";

export interface OTPModalState {
  // State
  otpCode: string;
  isSubmitting: boolean;
  otpApiErrors: string[];
  otpApiErrorCode: number;
  statusOtp: string;
  isCountingDown: boolean;
  startCountdown: boolean;
  showOTPInputModal: boolean;
}

export interface UseOTPModalReturn {
  // State object
  state: OTPModalState;

  // Setter function
  setState: (updates: Partial<OTPModalState>) => void;

  // Handlers
  handleOtpChange: (otp: string) => void;
  handleCountdownStart: () => void;
  handleCountdownFinish: () => void;
  handleCloseModal: () => void;
  handleOpenModal: () => void;

  // Reset function
  resetOTPState: () => void;
}

const INITIAL_STATE: OTPModalState = {
  otpCode: "",
  isSubmitting: false,
  otpApiErrors: [],
  otpApiErrorCode: 0,
  statusOtp: "",
  isCountingDown: false,
  startCountdown: false,
  showOTPInputModal: false
};

export const useOTPModal = (): UseOTPModalReturn => {
  const [state, setState] = useReducer(
    (state: OTPModalState, updates: Partial<OTPModalState>) => ({ ...state, ...updates }),
    INITIAL_STATE
  );

  const handleOtpChange = useCallback(
    (otp: string) => {
      setState({
        otpCode: otp,
        // Clear errors when user starts typing
        ...(state.otpApiErrors.length > 0 && {
          otpApiErrors: [],
          otpApiErrorCode: 0
        })
      });
    },
    [state.otpApiErrors.length]
  );

  const handleCountdownStart = useCallback(() => {
    setState({ isCountingDown: true });
  }, []);

  const handleCountdownFinish = useCallback(() => {
    setState({
      isCountingDown: false,
      startCountdown: false
    });
  }, []);

  const handleCloseModal = useCallback(() => {
    setState({ showOTPInputModal: false });
  }, []);

  const handleOpenModal = useCallback(() => {
    setState({ showOTPInputModal: true });
  }, []);

  const resetOTPState = useCallback(() => {
    setState(INITIAL_STATE);
  }, []);

  return {
    // State object
    state,

    // Setter function
    setState,

    // Handlers
    handleOtpChange,
    handleCountdownStart,
    handleCountdownFinish,
    handleCloseModal,
    handleOpenModal,

    // Reset function
    resetOTPState
  };
};
